import { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, IndianRupee, Package, TrendingUp, Receipt, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const PlanPurchaseHistory = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [totalSpent, setTotalSpent] = useState(0);

  useEffect(() => {
    fetchPurchaseHistory();
  }, []);

  const fetchPurchaseHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/my-plan-purchase-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchaseHistory(response.data.purchase_history || []);
      setTotalSpent(response.data.total_spent || 0);
    } catch (error) {
      console.error('Failed to fetch purchase history:', error);
      toast.error('Failed to load purchase history');
    } finally {
      setLoading(false);
    }
  };

  const getPlanBadgeColor = (planType) => {
    switch (planType?.toLowerCase()) {
      case 'basic':
        return 'bg-blue-500';
      case 'standard':
        return 'bg-purple-500';
      case 'premium':
        return 'bg-orange-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-contribo-navy to-contribo-gold text-white py-6 px-4 shadow-lg">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Receipt className="h-8 w-8" />
            <h1 className="text-3xl font-bold">Plan Purchase History</h1>
          </div>
          <p className="text-white text-opacity-90">View all your plan purchases and spending</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Summary Card */}
        <Card className="mb-6 border-2 border-contribo-navy shadow-xl">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Purchases</p>
                  <p className="text-2xl font-bold text-gray-900">{purchaseHistory.length}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-full">
                  <IndianRupee className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Spent</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalSpent)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Purchase History List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-contribo-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading purchase history...</p>
          </div>
        ) : purchaseHistory.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Receipt className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Purchase History</h3>
              <p className="text-gray-500">You haven't purchased any plans yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {purchaseHistory.map((purchase) => (
              <Card key={purchase.id} className="border-l-4 border-l-green-500 hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-lg font-bold text-gray-900">
                        {purchase.event_title || 'Event'}
                      </CardTitle>
                      <p className="text-sm text-gray-600 mt-1">Code: {purchase.event_code}</p>
                    </div>
                    <Badge className={`${getPlanBadgeColor(purchase.plan_type)} text-white`}>
                      {purchase.plan_type?.toUpperCase() || 'PLAN'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-gray-600">Plan Amount</p>
                        <p className="font-semibold text-gray-900">{formatCurrency(purchase.plan_amount)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <IndianRupee className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-gray-600">Final Amount</p>
                        <p className="font-semibold text-green-600">{formatCurrency(purchase.final_amount)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-gray-600">Event Date</p>
                        <p className="font-semibold text-gray-900">{purchase.event_date || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-gray-600">Purchased On</p>
                        <p className="font-semibold text-gray-900">{formatDate(purchase.created_at)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Budget Info */}
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center gap-2 text-sm">
                      <TrendingUp className="h-4 w-4 text-blue-500" />
                      <span className="text-gray-600">Total Budget:</span>
                      <span className="font-bold text-blue-600">{formatCurrency(purchase.total_budget)}</span>
                    </div>
                  </div>

                  {/* Coupon Info */}
                  {purchase.coupon_code && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-xs text-green-700">
                        🎉 Coupon Applied: <strong>{purchase.coupon_code}</strong>
                        {purchase.discount_amount > 0 && (
                          <span className="ml-2">
                            (Saved {formatCurrency(purchase.discount_amount)})
                          </span>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Payment Status */}
                  <div className="mt-3 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-xs font-semibold text-green-600">VERIFIED & PAID</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanPurchaseHistory;
